/* eslint-env node */
// Per-teacher Opus1 trial slugs that must not be offered for new trials.
// Francisca and Taylor are no longer on the public site (not in team.js); the
// list stays as a defensive guard for old /w/trial-* links and ?trial= params.
// CommonJS on purpose. Vercel loads api/chat.js with require(), and require()
// of an ESM file throws ERR_REQUIRE_ESM. See membershipRates.js.
//
// TrialSchedulerModal, Clara's reply filter (api/chat.js), and the chat
// renderer (ClaraChat.js) all import this module so the list cannot drift.
//
// Matching strips invisible/bidi/variation characters and applies NFKC, then
// compares /w/ slugs to an allowlist. Replacements are original-string spans.
// The normalized copy is never written back.

const BLOCKED_TRIAL_SLUGS = Object.freeze(['francisca', 'taylor']);
const TRIAL_PROMO_ID = '2cc064f7-e065-4dbe-9544-7cfe709ca763';
const GENERIC_TRIAL_URL = `https://sflcm.opus1.io/w/trial?promoId=${TRIAL_PROMO_ID}`;

// Exact booking slugs from src/data/team.js trial() and src/data/pricing.js
// instrumentTrial() opus paths, plus the generic /w/trial page. Francisca and
// Taylor are not in this set. Anything else under /w/ fail-closes to GENERIC_TRIAL_URL.
const TEACHER_TRIAL_SLUGS = Object.freeze([
  'yilia',
  'james',
  'vivian',
  'aramis',
  'blake',
  'gabriela-t',
  'isaac',
  'mauricio',
  'yaron',
  'yaymel',
  'fernando',
  'ashley',
]);
const INSTRUMENT_W_SLUGS = Object.freeze([
  'trialpiano',
  'trialviolin',
  'trialguitar',
  'trialsinging',
  'trialdrums',
  'trialcello',
]);
const ALLOWED_W_SLUGS = new Set([
  'trial',
  ...TEACHER_TRIAL_SLUGS.map((slug) => `trial-${slug}`),
  ...INSTRUMENT_W_SLUGS,
]);

const OPUS_HOST = 'sflcm.opus1.io';
const MAX_HOLD = 512;
const TRAIL_CHARS = '.,;:!?_*~`';

function isInvisible(cp) {
  return (cp >= 0x200B && cp <= 0x200D)
    || cp === 0x2060
    || cp === 0xFEFF
    || cp === 0x00AD
    || cp === 0x200E
    || cp === 0x200F
    || (cp >= 0x202A && cp <= 0x202E)
    || (cp >= 0x2066 && cp <= 0x2069)
    || cp === 0x034F
    || cp === 0x061C
    || cp === 0x180E
    || (cp >= 0xFE00 && cp <= 0xFE0F)
    || (cp >= 0xE0000 && cp <= 0xE007F);
}

function cpWidth(cp) {
  return cp > 0xFFFF ? 2 : 1;
}

function codePointBefore(text, index) {
  if (index <= 0) return -1;
  const prev = index - 1;
  const lead = text.charCodeAt(prev);
  if (lead >= 0xDC00 && lead <= 0xDFFF && prev > 0) {
    const hi = text.charCodeAt(prev - 1);
    if (hi >= 0xD800 && hi <= 0xDBFF) return text.codePointAt(prev - 1);
  }
  return text.codePointAt(prev);
}

// Strip invisibles, NFKC, lowercase. Length may change. Callers that replace
// text must keep using original offsets from the scanner, not this string.
function normalizeMatch(text) {
  let out = '';
  for (let i = 0; i < text.length;) {
    const cp = text.codePointAt(i);
    const width = cpWidth(cp);
    if (!isInvisible(cp)) {
      out += String.fromCodePoint(cp).normalize('NFKC').toLowerCase();
    }
    i += width;
  }
  return out;
}

function safeDecode(value) {
  let current = String(value ?? '');
  for (let round = 0; round < 3; round += 1) {
    let next;
    try {
      next = decodeURIComponent(current);
    } catch (err) {
      break;
    }
    if (next === current) break;
    current = next;
  }
  return current;
}

function normalizeWSlug(segment) {
  let slug = normalizeMatch(safeDecode(normalizeMatch(String(segment ?? ''))));
  slug = slug.replace(/[?#].*$/, '').replace(/\/+$/, '').replace(/[.,;:!?]+$/g, '').trim();
  return slug;
}

// trim, lowercase, decode, then strip a leading "trial" or "-" until stable.
function normalizeTrialSlug(raw) {
  let slug = normalizeWSlug(raw);
  let guard = 0;
  while (slug && guard < 8) {
    if (slug.startsWith('trial')) slug = slug.slice(5).trim();
    else if (slug.startsWith('-')) slug = slug.slice(1).trim();
    else break;
    guard += 1;
  }
  return slug;
}

function isBlockedTrialSlug(raw) {
  const slug = normalizeTrialSlug(raw);
  if (!slug) return false;
  // Any slug that contains her name (francisca_balass, a trailing wrapper
  // that survived parsing, …). Other blocked slugs stay exact.
  if (slug.includes('francisca')) return true;
  return BLOCKED_TRIAL_SLUGS.some((blocked) => (
    slug === blocked || slug.startsWith(`${blocked}-`)
  ));
}

// Names that make a rewritten trial link's label generic ('Book a Trial
// Lesson'). Only applied to links already being rewritten to the generic URL.
const BLOCKED_LABEL_NAMES = Object.freeze(['francisca', 'taylor']);

function labelContainsFrancisca(label) {
  const norm = normalizeMatch(String(label ?? ''));
  return BLOCKED_LABEL_NAMES.some((name) => norm.includes(name));
}

function isTerminator(cp) {
  if (isInvisible(cp)) return false;
  const ch = String.fromCodePoint(cp).normalize('NFKC');
  if (/\s/u.test(ch)) return true;
  return '<>"\'()[]*`~'.includes(ch);
}

function isEntityAt(text, index) {
  return /^(?:&gt;|&lt;|&amp;|&quot;|&apos;|&nbsp;|&#\d+;|&#x[0-9a-f]+;)/i.test(
    text.slice(index, index + 16),
  );
}

function isHostChar(cp) {
  if (isInvisible(cp)) return false;
  const folded = String.fromCodePoint(cp).normalize('NFKC').toLowerCase();
  if (folded.length !== 1) return false;
  const code = folded.charCodeAt(0);
  return (code >= 48 && code <= 57)
    || (code >= 97 && code <= 122)
    || folded === '.'
    || folded === '-'
    || folded === '@';
}

function hasUrlBoundary(text, index) {
  let cursor = index;
  while (cursor > 0) {
    const prev = codePointBefore(text, cursor);
    if (prev < 0) return true;
    const width = cpWidth(prev);
    if (!isInvisible(prev)) return !isHostChar(prev);
    cursor -= width;
  }
  return true;
}

function isUrlStartChar(text, index) {
  const cp = text.codePointAt(index);
  if (cp === undefined || isInvisible(cp)) return false;
  const folded = String.fromCodePoint(cp).normalize('NFKC').toLowerCase();
  return folded === 'h' || folded === '/' || folded === 'w' || folded === 's';
}

// Read one URL token in original coordinates. Internal invisibles stay inside
// the span. Trailing prose punctuation and a following HTML entity do not.
function consumeToken(text, start) {
  let i = start;
  let norm = '';
  while (i < text.length) {
    const cp = text.codePointAt(i);
    const width = cpWidth(cp);
    if (isInvisible(cp)) {
      let k = i + width;
      while (k < text.length && isInvisible(text.codePointAt(k))) {
        k += cpWidth(text.codePointAt(k));
      }
      if (k >= text.length || isTerminator(text.codePointAt(k)) || isEntityAt(text, k)) break;
      i += width;
      continue;
    }
    if (isTerminator(cp) || isEntityAt(text, i)) break;
    norm += String.fromCodePoint(cp).normalize('NFKC').toLowerCase();
    i += width;
  }
  let end = i;
  while (end > start) {
    const prev = codePointBefore(text, end);
    if (prev < 0) break;
    const ch = String.fromCodePoint(prev).normalize('NFKC');
    if (ch.length !== 1 || !TRAIL_CHARS.includes(ch)) break;
    end -= cpWidth(prev);
    norm = norm.slice(0, -ch.length);
  }
  if (!norm) return null;
  return { start, end, norm };
}

function firstSeparator(text) {
  const slash = text.indexOf('/');
  const back = text.indexOf('\\');
  if (slash < 0) return back;
  if (back < 0) return slash;
  return slash < back ? slash : back;
}

// https://, http://, a single slash (https:/), a bare scheme (https:), or //.
function withoutScheme(value) {
  const text = String(value ?? '');
  if (text.startsWith('https://')) return text.slice(8);
  if (text.startsWith('http://')) return text.slice(7);
  if (text.startsWith('https:/')) return text.slice(7);
  if (text.startsWith('http:/')) return text.slice(6);
  if (text.startsWith('https:')) return text.slice(6);
  if (text.startsWith('http:')) return text.slice(5);
  if (text.startsWith('//')) return text.slice(2);
  return text;
}

// new URL() needs an absolute URL. Malformed https: / https:/ forms already
// parse. Schemeless and protocol-relative forms need a scheme added.
// "sflcm.opus1.io:/path" is an empty port, not a scheme, so only http(s) passes through.
function absoluteUrl(value) {
  const text = String(value ?? '');
  if (/^https?:/i.test(text)) return text;
  if (text.startsWith('//')) return `https:${text}`;
  return `https://${text.replace(/^\/+/, '')}`;
}

// Strip a numeric or empty port and one trailing dot. Empty ("host:") must
// not stay on the host or the string compare misses Opus before new URL().
function canonicalHost(host) {
  let value = normalizeMatch(safeDecode(host));
  if (value.startsWith('www.')) value = value.slice(4);
  const colon = value.lastIndexOf(':');
  if (colon >= 0 && /^\d*$/.test(value.slice(colon + 1))) value = value.slice(0, colon);
  if (value.endsWith('.')) value = value.slice(0, -1);
  return value;
}

function pathSegments(path) {
  const queryAt = String(path ?? '').search(/[?#]/);
  const bare = queryAt >= 0 ? String(path).slice(0, queryAt) : String(path ?? '');
  return bare.split(/[\\/]+/).filter(Boolean).map((segment) => normalizeWSlug(segment));
}

function segmentsContainFrancisca(segments) {
  return segments.some((segment) => isBlockedTrialSlug(segment));
}

// True when the raw authority is Opus: the host, or userinfo that is itself
// the Opus host (`https://sflcm.opus1.io@evil.com/...`). Port and a trailing
// dot are ignored. Opus-ness of a parsed URL still comes from hostname.
function rawHostIsOpus(value) {
  let rest = withoutScheme(String(value ?? ''));
  const sepAt = firstSeparator(rest);
  const at = rest.indexOf('@');
  if (at >= 0 && (sepAt < 0 || at < sepAt)) {
    const userinfo = rest.slice(0, at);
    rest = rest.slice(at + 1);
    const sep = firstSeparator(rest);
    const host = sep < 0 ? rest : rest.slice(0, sep);
    return canonicalHost(userinfo) === OPUS_HOST || canonicalHost(host) === OPUS_HOST;
  }
  const sep = firstSeparator(rest);
  const host = sep < 0 ? rest : rest.slice(0, sep);
  return canonicalHost(host) === OPUS_HOST;
}

// `norm` is already invisible-stripped and NFKC'd. Whether the host is Opus
// comes from new URL().hostname, not the raw host string, so an empty port
// (`host:/`, `host.:/`, `host:\`) still matches. The allowlist runs on the
// resolved pathname. A francisca segment in the raw path or the resolved
// path still rewrites. An Opus candidate that new URL() cannot parse, or
// whose parsed hostname is not Opus, fail-closes.
function analyze(norm) {
  const value = String(norm ?? '');
  const body = withoutScheme(value);
  const hadScheme = body !== value;
  if (!hadScheme && !(body.startsWith('www.') || body.startsWith('sflcm.') || body.startsWith(OPUS_HOST))) {
    return { opus: false, rewrite: false };
  }

  let rest = body;
  const sepAt = firstSeparator(rest);
  const at = rest.indexOf('@');
  if (at >= 0 && (sepAt < 0 || at < sepAt)) rest = rest.slice(at + 1);
  const sep = firstSeparator(rest);
  const rawPath = sep < 0 ? '' : rest.slice(sep);
  const rawOpus = rawHostIsOpus(value);

  let parsed;
  try {
    parsed = new URL(absoluteUrl(value));
  } catch (err) {
    if (rawOpus) return { opus: true, rewrite: true };
    return { opus: false, rewrite: false };
  }

  const parsedHost = canonicalHost(parsed.hostname);
  if (parsedHost !== OPUS_HOST) {
    if (rawOpus) return { opus: true, rewrite: true };
    return { opus: false, rewrite: false };
  }

  const rawSegments = pathSegments(rawPath);
  const resolvedSegments = pathSegments(parsed.pathname);
  if (segmentsContainFrancisca(rawSegments) || segmentsContainFrancisca(resolvedSegments)) {
    return { opus: true, rewrite: true };
  }
  if (resolvedSegments.length === 0 || resolvedSegments[0] !== 'w') return { opus: true, rewrite: false };
  if (resolvedSegments.length !== 2) return { opus: true, rewrite: true };
  if (!ALLOWED_W_SLUGS.has(resolvedSegments[1])) return { opus: true, rewrite: true };
  return { opus: true, rewrite: false };
}

function shouldRewriteOpusTrialUrl(urlString) {
  const peeled = String(urlString ?? '').replace(/[.,;:!?_*~`]+$/g, '');
  return analyze(normalizeMatch(peeled)).rewrite;
}

function trialUrlResolvesToBlockedSlug(urlString) {
  const normalized = normalizeMatch(String(urlString ?? ''));
  let parsed;
  try {
    const candidate = /^(https?:)?\/\//i.test(normalized)
      ? normalized
      : `https://${normalized.replace(/^\/+/, '')}`;
    parsed = new URL(candidate);
  } catch (err) {
    return false;
  }
  const host = parsed.hostname.replace(/\.$/, '').replace(/^www\./, '').toLowerCase();
  if (host !== OPUS_HOST) return false;
  const segments = parsed.pathname.split('/').filter(Boolean);
  return segments.some((segment) => isBlockedTrialSlug(segment));
}

// Markdown and prose can cling to a URL: **url**, `url`, _url_, ~~url~~,
// and the same with quotes or angle brackets. Those characters are not
// part of the URL. Parentheses and square brackets are already outside
// the match. "_" is also a real slug character (trial-francisca_balass),
// so it is removed only when it is trailing, then put back around the
// replacement.
function splitTrailingPunct(match) {
  let url = match;
  let trail = '';
  while (url && /[.,;:!?*`_~<>"']$/.test(url)) {
    trail = url.slice(-1) + trail;
    url = url.slice(0, -1);
  }
  return [url, trail];
}

function rangeOverlaps(ranges, start, end) {
  return ranges.some((range) => start < range.end && end > range.start);
}

function applyReplacements(input, reps) {
  const sorted = reps
    .filter((rep) => rep && rep.end > rep.start && typeof rep.replacement === 'string')
    .sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  let out = '';
  let cursor = 0;
  sorted.forEach((rep) => {
    if (rep.start < cursor) return;
    out += input.slice(cursor, rep.start);
    out += rep.replacement;
    cursor = rep.end;
  });
  out += input.slice(cursor);
  return out;
}

function labelReplacement(label, depth) {
  if (labelContainsFrancisca(label)) return 'Book a Trial Lesson';
  const rewritten = rewriteBlockedTrialUrls(label, depth + 1);
  return rewritten === label ? null : rewritten;
}

function collectMarkdown(text, reps, covered, depth) {
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '[') continue;
    const close = text.indexOf(']', i + 1);
    if (close < 0) continue;
    const openParen = skipInvisibles(text, close + 1);
    if (text[openParen] !== '(') continue;
    const paren = text.indexOf(')', openParen + 1);
    if (paren < 0) continue;
    const start = i;
    const end = paren + 1;
    const label = text.slice(i + 1, close);
    const url = text.slice(openParen + 1, paren);
    if (analyze(normalizeMatch(url)).rewrite) {
      const nextLabel = labelReplacement(label, depth);
      const shown = nextLabel == null ? label : nextLabel;
      reps.push({ start, end, replacement: `[${shown}](${GENERIC_TRIAL_URL})` });
    }
    covered.push({ start, end });
    i = end - 1;
  }
}

function collectReferences(text, reps, covered, depth) {
  const rewrittenIds = [];
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '[') continue;
    if (rangeOverlaps(covered, i, i + 1)) continue;
    const close = text.indexOf(']', i + 1);
    const colon = close < 0 ? -1 : skipInvisibles(text, close + 1);
    if (close < 0 || text[colon] !== ':') continue;
    let urlAt = colon + 1;
    while (urlAt < text.length && (text[urlAt] === ' ' || text[urlAt] === '\t')) urlAt += 1;
    const token = consumeToken(text, urlAt);
    if (!token) continue;
    if (analyze(token.norm).rewrite && !rangeOverlaps(covered, token.start, token.end)) {
      reps.push({ start: token.start, end: token.end, replacement: GENERIC_TRIAL_URL });
      rewrittenIds.push(normalizeMatch(text.slice(i + 1, close)));
    }
    covered.push({ start: token.start, end: token.end });
    i = Math.max(i, token.end - 1);
  }
  if (!rewrittenIds.length) return;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== '[') continue;
    const close = text.indexOf(']', i + 1);
    if (close < 0 || text[close + 1] !== '[') continue;
    const idEnd = text.indexOf(']', close + 2);
    if (idEnd < 0) continue;
    const id = normalizeMatch(text.slice(close + 2, idEnd));
    if (!rewrittenIds.includes(id)) continue;
    const label = text.slice(i + 1, close);
    const nextLabel = labelReplacement(label, depth);
    if (nextLabel != null) reps.push({ start: i + 1, end: close, replacement: nextLabel });
    i = idEnd;
  }
}

function readHref(openTag) {
  const lower = openTag.toLowerCase();
  const key = lower.indexOf('href');
  if (key < 0) return null;
  let i = key + 4;
  while (i < openTag.length && /\s/.test(openTag[i])) i += 1;
  if (openTag[i] !== '=') return null;
  i += 1;
  while (i < openTag.length && /\s/.test(openTag[i])) i += 1;
  const quote = openTag[i];
  if (quote !== '"' && quote !== "'") return null;
  const valueStart = i + 1;
  const valueEnd = openTag.indexOf(quote, valueStart);
  if (valueEnd < 0) return null;
  return { valueStart, valueEnd };
}

function collectHtml(text, reps, covered, depth) {
  const lower = text.toLowerCase();
  let from = 0;
  while (from < text.length) {
    const at = lower.indexOf('<a', from);
    if (at < 0) break;
    const boundary = text[at + 2];
    if (boundary && /[a-z0-9]/i.test(boundary)) {
      from = at + 2;
      continue;
    }
    const openEnd = text.indexOf('>', at + 2);
    if (openEnd < 0) break;
    const href = readHref(text.slice(at, openEnd + 1));
    const close = lower.indexOf('</a>', openEnd + 1);
    if (!href || close < 0) {
      from = openEnd + 1;
      continue;
    }
    const urlStart = at + href.valueStart;
    const urlEnd = at + href.valueEnd;
    const labelStart = openEnd + 1;
    const labelEnd = close;
    if (analyze(normalizeMatch(text.slice(urlStart, urlEnd))).rewrite) {
      reps.push({ start: urlStart, end: urlEnd, replacement: GENERIC_TRIAL_URL });
      const nextLabel = labelReplacement(text.slice(labelStart, labelEnd), depth);
      if (nextLabel != null) reps.push({ start: labelStart, end: labelEnd, replacement: nextLabel });
    }
    covered.push({ start: at, end: close + 4 });
    from = close + 4;
  }
}

function collectBare(text, reps, covered) {
  let i = 0;
  while (i < text.length) {
    if (!isUrlStartChar(text, i) || !hasUrlBoundary(text, i)) {
      const cp = text.codePointAt(i);
      i += cp === undefined ? 1 : cpWidth(cp);
      continue;
    }
    const token = consumeToken(text, i);
    if (!token) {
      i += 1;
      continue;
    }
    if (!rangeOverlaps(covered, token.start, token.end) && analyze(token.norm).rewrite) {
      reps.push({ start: token.start, end: token.end, replacement: GENERIC_TRIAL_URL });
    }
    i = Math.max(i + 1, token.end);
  }
}

function rewriteBlockedTrialUrls(text, depth) {
  const input = String(text ?? '');
  if ((depth || 0) > 2 || !input) return input;
  const reps = [];
  const covered = [];
  collectMarkdown(input, reps, covered, depth || 0);
  collectReferences(input, reps, covered, depth || 0);
  collectHtml(input, reps, covered, depth || 0);
  collectBare(input, reps, covered);
  if (!reps.length) return input;
  return applyReplacements(input, reps);
}

function isWhitespace(cp) {
  // U+FEFF is both an invisible we strip and a Unicode WhiteSpace. It must not
  // split a URL that hides it on a boundary.
  if (cp < 0 || isInvisible(cp)) return false;
  return /\s/u.test(String.fromCodePoint(cp));
}

function skipInvisibles(text, index) {
  let i = index;
  while (i < text.length && isInvisible(text.codePointAt(i))) {
    i += cpWidth(text.codePointAt(i));
  }
  return i;
}

// True when this suffix can still grow into an Opus1 trial URL. A finished
// non-Opus host is released. A real Opus URL stays unfinished until whitespace
// or flush, because the slug can still grow (trial → trial-francisca).
function isUnfinishedOpusCandidate(raw) {
  const norm = normalizeMatch(raw);
  if (!norm) return false;
  // `https:` and `https:/` are not held as prefixes of `https://`. These
  // anchors are the only reason a chunk that is exactly that scheme stays held.
  const malformedSchemes = ['https:', 'https:/', 'http:', 'http:/'];
  if (malformedSchemes.indexOf(norm) >= 0) return true;
  const anchors = [
    'https://',
    'http://',
    `//www.${OPUS_HOST}`,
    `//${OPUS_HOST}`,
    `www.${OPUS_HOST}`,
    OPUS_HOST,
  ];
  for (let a = 0; a < anchors.length; a += 1) {
    if (malformedSchemes.indexOf(norm) >= 0) continue;
    if (anchors[a].startsWith(norm) && norm.length < anchors[a].length) return true;
  }

  const stripped = withoutScheme(norm);
  const scheme = stripped !== norm;
  let body = stripped;
  if (!scheme && !(body.startsWith('www.') || body.startsWith('sflcm'))) return false;

  const slash = firstSeparator(body);
  const at = body.indexOf('@');
  if (at >= 0 && (slash < 0 || at < slash)) body = body.slice(at + 1);
  const slashAfter = firstSeparator(body);
  let host = slashAfter < 0 ? body : body.slice(0, slashAfter);
  host = canonicalHost(host);
  if (!host) return scheme || slashAfter < 0;
  if (host === OPUS_HOST || (OPUS_HOST.startsWith(host) && host.length < OPUS_HOST.length)) return true;
  // "https://x" can still be userinfo ("x@sflcm.opus1.io") until a dot, slash, or @ arrives.
  if (scheme && !norm.includes('@') && !host.includes('.') && slashAfter < 0) return true;
  return false;
}

function completeMarkdownEnd(win, start) {
  const close = win.indexOf(']', start + 1);
  if (close < 0) return -1;
  const j = skipInvisibles(win, close + 1);
  if (j >= win.length) return -1;
  if (win[j] === '(') {
    const paren = win.indexOf(')', j + 1);
    if (paren < 0) return -1;
    return paren + 1;
  }
  if (win[j] === ':') {
    let k = j + 1;
    while (k < win.length && (win[k] === ' ' || win[k] === '\t')) k += 1;
    if (k >= win.length) return -1;
    let t = k;
    while (t < win.length && !isWhitespace(win.codePointAt(t))) {
      t += cpWidth(win.codePointAt(t));
    }
    if (t >= win.length) return -1;
    return t;
  }
  if (win[j] === '[') {
    const idEnd = win.indexOf(']', j + 1);
    if (idEnd < 0) return -1;
    const label = win.slice(start + 1, close);
    if (!labelContainsFrancisca(label)) return idEnd + 1;
    const id = normalizeMatch(win.slice(j + 1, idEnd));
    const defAt = referenceDefAt(win, idEnd + 1, id);
    if (defAt < 0) return -1;
    const defEnd = referenceDefEnd(win, defAt);
    if (defEnd < 0) return -1;
    return defEnd;
  }
  return close + 1;
}

function markdownHoldFrom(win) {
  for (let i = 0; i < win.length; i += 1) {
    if (win[i] !== '[') continue;
    const end = completeMarkdownEnd(win, i);
    if (end < 0) return i;
    i = end - 1;
  }
  return -1;
}

function referenceDefAt(win, from, id) {
  const needle = `[${id}]:`;
  const lower = win.toLowerCase();
  let cursor = from;
  while (cursor < win.length) {
    const at = lower.indexOf(needle, cursor);
    if (at < 0) return -1;
    if (at === 0 || /\s/.test(win[at - 1])) return at;
    cursor = at + 1;
  }
  return -1;
}

function referenceDefEnd(win, defAt) {
  const close = win.indexOf(']', defAt + 1);
  if (close < 0 || win[skipInvisibles(win, close + 1)] !== ':') return -1;
  return completeMarkdownEnd(win, defAt);
}

function angleLooksLikeLink(inner) {
  const norm = normalizeMatch(inner);
  if (!norm) return true;
  const hints = ['https://', 'http://', '//', OPUS_HOST, `www.${OPUS_HOST}`, 'a href', 'a '];
  return hints.some((hint) => hint.startsWith(norm) || norm.startsWith(hint));
}

function angleHoldFrom(win) {
  for (let i = 0; i < win.length; i += 1) {
    if (win[i] !== '<') continue;
    const close = win.indexOf('>', i + 1);
    if (close >= 0) {
      i = close;
      continue;
    }
    if (angleLooksLikeLink(win.slice(i + 1, i + 24))) return i;
  }
  return -1;
}

function urlHoldFrom(win) {
  let tokenStart = 0;
  for (let i = 0; i < win.length;) {
    const cp = win.codePointAt(i);
    const width = cpWidth(cp);
    if (isWhitespace(cp)) tokenStart = i + width;
    i += width;
  }
  const token = win.slice(tokenStart);
  let offset = 0;
  while (offset < token.length) {
    const cp = token.codePointAt(offset);
    const ch = String.fromCodePoint(cp);
    if (isInvisible(cp) || ch === '*' || ch === '_' || ch === '~' || ch === '`') {
      offset += cpWidth(cp);
      continue;
    }
    break;
  }
  if (isUnfinishedOpusCandidate(token.slice(offset))) return tokenStart;
  return -1;
}

function trailingUnfinishedOpusUrl(text) {
  if (!text) return 0;
  const winStart = Math.max(0, text.length - MAX_HOLD);
  const win = text.slice(winStart);
  let holdAt = -1;
  const markdownAt = markdownHoldFrom(win);
  const angleAt = angleHoldFrom(win);
  const urlAt = urlHoldFrom(win);
  [markdownAt, angleAt, urlAt].forEach((at) => {
    if (at >= 0 && (holdAt < 0 || at < holdAt)) holdAt = at;
  });
  if (holdAt < 0) return 0;
  return win.length - holdAt;
}

// Holds a trailing Opus1 URL so a blocked link split across stream chunks
// is rewritten before any of it is forwarded. The hold is capped at MAX_HOLD.
function createTrialReplyFilter() {
  let pending = '';
  return {
    push(chunk) {
      pending += String(chunk ?? '');
      const hold = Math.min(trailingUnfinishedOpusUrl(pending), MAX_HOLD, pending.length);
      const ready = pending.slice(0, pending.length - hold);
      pending = pending.slice(pending.length - hold);
      return rewriteBlockedTrialUrls(ready);
    },
    flush() {
      const ready = pending;
      pending = '';
      return rewriteBlockedTrialUrls(ready);
    },
  };
}

exports.BLOCKED_TRIAL_SLUGS = BLOCKED_TRIAL_SLUGS;
exports.GENERIC_TRIAL_URL = GENERIC_TRIAL_URL;
exports.TRIAL_PROMO_ID = TRIAL_PROMO_ID;
exports.TEACHER_TRIAL_SLUGS = TEACHER_TRIAL_SLUGS;
exports.INSTRUMENT_W_SLUGS = INSTRUMENT_W_SLUGS;
exports.ALLOWED_W_SLUGS = ALLOWED_W_SLUGS;
exports.normalizeTrialSlug = normalizeTrialSlug;
exports.isBlockedTrialSlug = isBlockedTrialSlug;
exports.labelContainsFrancisca = labelContainsFrancisca;
exports.BLOCKED_LABEL_NAMES = BLOCKED_LABEL_NAMES;
exports.trialUrlResolvesToBlockedSlug = trialUrlResolvesToBlockedSlug;
exports.shouldRewriteOpusTrialUrl = shouldRewriteOpusTrialUrl;
exports.splitTrailingPunct = splitTrailingPunct;
exports.rewriteBlockedTrialUrls = rewriteBlockedTrialUrls;
exports.createTrialReplyFilter = createTrialReplyFilter;
